/**
 * Parsing History Tab Component
 * 
 * Displays parsing history for a specific project
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Descriptions,
  Alert,
  Progress,
  Drawer,
} from 'antd'
import {
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  DownloadOutlined,
  UserOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import api from '@/services/api'
import { getRegionName } from '@/utils/regions'
import ParsingWorkflowModal from '@/components/ParsingWorkflowModal'

const { Text } = Typography

interface ParsingHistoryTabProps {
  projectId: number
  projectCrmId?: number
  projectName?: string
}

interface ParsingTask {
  task_id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'uploaded'
  crm_id: number
  project_id?: number
  project_name?: string
  operator_id?: number
  operator_username?: string
  main_query: string
  region_code: string
  progress: number
  current_step?: string
  error_message?: string
  created_at?: string
  task_metadata?: {
    min_clicks_threshold?: number
    download_all_queries?: boolean
    top_queries_count?: number
  }
}

interface ParsingResult {
  success: boolean
  main_query: string
  region_code: string
  region_name: string
  domains: any[]
  urls: any[]
  queries: any[]
  domains_count: number
  urls_count: number
  queries_count: number
  started_at: string
  completed_at: string
  duration_seconds: number
}

const statusColors: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  uploaded: 'cyan',
}

const statusIcons: Record<string, any> = {
  pending: <ClockCircleOutlined />,
  running: <RocketOutlined spin />,
  completed: <CheckCircleOutlined />,
  uploaded: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
}

function ParsingHistoryTab({ projectId, projectCrmId, projectName }: ParsingHistoryTabProps) {
  const [tasks, setTasks] = useState<ParsingTask[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedTask, setSelectedTask] = useState<ParsingTask | null>(null)
  const [result, setResult] = useState<ParsingResult | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [loadingResult, setLoadingResult] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [hasRunningTasks, setHasRunningTasks] = useState(false)
  const [workflowVisible, setWorkflowVisible] = useState(false)

  useEffect(() => {
    loadTasks()
    
    const interval = setInterval(() => {
      if (hasRunningTasks) {
        console.log('🔄 Auto-refreshing tasks (running tasks detected)...')
        loadTasks()
      }
    }, 5000)
    
    return () => clearInterval(interval)
  }, [hasRunningTasks, projectId])

  const loadTasks = async () => {
    try {
      setLoading(true)
      
      const data = await api.getParsingTasks()
      
      console.log('📊 Parsing tasks loaded:', data)
      
      const tasksList = data.tasks || []
      
      // * Filter tasks by project_id
      const filteredTasks = tasksList.filter((t: ParsingTask) => 
        t.project_id === projectId
      )
      
      setTasks(filteredTasks)
      
      const hasRunning = filteredTasks.some((t: ParsingTask) => 
        t.status === 'running' || t.status === 'pending'
      )
      setHasRunningTasks(hasRunning)
      
      console.log(`✅ Loaded ${filteredTasks.length} tasks for project ${projectId}, running: ${hasRunning}`)
      
    } catch (error: any) {
      console.error('❌ Error loading tasks:', error)
      console.error('Error details:', error.response?.data || error.message)
      
      if (tasks.length === 0) {
        console.log('ℹ️ No tasks yet (first load)')
      } else {
        message.error('Ошибка загрузки задач')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadResult = async (task: ParsingTask) => {
    try {
      setLoadingResult(true)
      setSelectedTask(task)
      setDrawerVisible(true)
      
      const resultData = await api.getParsingResult(task.task_id)
      setResult(resultData)
    } catch (error: any) {
      console.error('❌ Error loading result:', error)
      
      if (error.response?.status === 404) {
        message.error('Результаты парсинга не найдены. Возможно, они еще не готовы или были удалены.')
      } else if (error.response?.status === 500) {
        message.error('Ошибка сервера при загрузке результатов. Попробуйте позже.')
      } else {
        message.error('Ошибка загрузки результатов: ' + (error.response?.data?.detail || error.message || 'Неизвестная ошибка'))
      }
      
      setResult(null)
    } finally {
      setLoadingResult(false)
    }
  }

  const handleUploadToVDL = async () => {
    if (!selectedTask || !result) return

    try {
      setUploading(true)
      setUploadProgress(0)
      setUploadStatus('Запуск загрузки...')
      
      const response = await api.processParsingResults(
        selectedTask.task_id,
        selectedTask.crm_id
      )
      
      if (response.success && response.upload_task_id) {
        const uploadTaskId = response.upload_task_id
        message.info('Загрузка в VDL запущена...')
        
        const pollInterval = setInterval(async () => {
          try {
            const status = await api.getUploadStatus(uploadTaskId)
            
            setUploadProgress(status.progress || 0)
            setUploadStatus(status.current_step || status.status)
            
            if (status.status === 'completed') {
              clearInterval(pollInterval)
              message.success(
                `✅ Загружено ${status.domains_uploaded} источников в VDL!`
              )
              setUploading(false)
              setDrawerVisible(false)
            } else if (status.status === 'failed') {
              clearInterval(pollInterval)
              message.error('Ошибка загрузки: ' + (status.error_message || 'Неизвестная ошибка'))
              setUploading(false)
            }
          } catch (error: any) {
            console.error('Error polling upload status:', error)
          }
        }, 2000)
        
        setTimeout(() => {
          clearInterval(pollInterval)
          if (uploading) {
            setUploading(false)
          }
        }, 300000)
      } else {
        throw new Error(response.error || 'Не получен upload_task_id')
      }
    } catch (error: any) {
      message.error('Ошибка запуска загрузки: ' + (error.message || 'Неизвестная ошибка'))
      setUploading(false)
    }
  }

  const columns: ColumnsType<ParsingTask> = [
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag icon={statusIcons[status]} color={statusColors[status]}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Запрос',
      dataIndex: 'main_query',
      key: 'main_query',
      ellipsis: true,
    },
    {
      title: 'Регион',
      dataIndex: 'region_code',
      key: 'region_code',
      width: 200,
      ellipsis: true,
      render: (regionCode: string) => (
        <span title={getRegionName(regionCode)}>
          {getRegionName(regionCode)}
        </span>
      ),
    },
    {
      title: 'Параметры парсера',
      key: 'parser_params',
      width: 220,
      render: (_: any, record: ParsingTask) => {
        const metadata = record.task_metadata
        if (!metadata || (!metadata.min_clicks_threshold && metadata.download_all_queries === undefined)) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <span style={{ opacity: 0.5 }}>По умолчанию</span>
            </Text>
          )
        }
        
        return (
          <Space direction="vertical" size={6} style={{ fontSize: 12, width: '100%' }}>
            {metadata.min_clicks_threshold !== undefined && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '4px 8px',
                background: metadata.min_clicks_threshold === 0 ? '#fff7e6' : '#e6f7ff',
                borderRadius: 4,
                border: `1px solid ${metadata.min_clicks_threshold === 0 ? '#ffd591' : '#91d5ff'}`
              }}>
                <span style={{ fontSize: 11, color: '#666' }}>🔽 Минимум кликов:</span>
                <Tag 
                  color={metadata.min_clicks_threshold === 0 ? 'warning' : 'blue'} 
                  style={{ margin: 0, fontSize: 11, fontWeight: 600 }}
                >
                  {metadata.min_clicks_threshold === 0 ? 'Без фильтра' : `${metadata.min_clicks_threshold}+`}
                </Tag>
              </div>
            )}
            {metadata.download_all_queries !== undefined && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '4px 8px',
                background: metadata.download_all_queries ? '#fff1f0' : '#f6ffed',
                borderRadius: 4,
                border: `1px solid ${metadata.download_all_queries ? '#ffccc7' : '#b7eb8f'}`
              }}>
                <span style={{ fontSize: 11, color: '#666' }}>📥 Режим:</span>
                <Tag 
                  color={metadata.download_all_queries ? 'red' : 'green'} 
                  style={{ margin: 0, fontSize: 11, fontWeight: 600 }}
                >
                  {metadata.download_all_queries ? 'Все запросы' : `ТОП-${metadata.top_queries_count || 100}`}
                </Tag>
              </div>
            )}
            {!metadata.download_all_queries && metadata.top_queries_count !== undefined && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '4px 8px',
                background: '#e6f7ff',
                borderRadius: 4,
                border: '1px solid #91d5ff'
              }}>
                <span style={{ fontSize: 11, color: '#666' }}>📊 ТОП-N:</span>
                <Tag color="cyan" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                  {metadata.top_queries_count} запросов
                </Tag>
              </div>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Оператор',
      dataIndex: 'operator_username',
      key: 'operator_username',
      width: 120,
      render: (username: string) => (
        username ? (
          <Space size={4}>
            <UserOutlined style={{ fontSize: 12 }} />
            <Text style={{ fontSize: 12 }}>{username}</Text>
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
        )
      ),
    },
    {
      title: 'Прогресс',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: ParsingTask) => {
        if (record.status === 'completed') {
          return <Progress percent={100} size="small" status="success" />
        }
        if (record.status === 'failed') {
          return <Progress percent={100} size="small" status="exception" />
        }
        return <Progress percent={progress} size="small" status="active" />
      },
    },
    {
      title: 'Создано',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (
        date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: ParsingTask) => (
        <Space size="small">
          {record.status === 'completed' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                onClick={async () => {
                  // * Load result and open workflow
                  try {
                    setLoadingResult(true)
                    setSelectedTask(record)
                    const resultData = await api.getParsingResult(record.task_id)
                    setResult(resultData)
                    setWorkflowVisible(true)
                  } catch (error: any) {
                    message.error('Ошибка загрузки результатов')
                  } finally {
                    setLoadingResult(false)
                  }
                }}
              >
                Открыть workflow
              </Button>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => loadResult(record)}
              >
                Просмотр
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadTasks}
            loading={loading}
          >
            Обновить
          </Button>
        </div>

        <Card>
          {tasks.length === 0 && !loading && (
            <Alert
              message="Нет задач парсинга для этого проекта"
              description={
                <div>
                  <p>Парсинги будут отображаться здесь после запуска.</p>
                  <p><strong>Как запустить:</strong></p>
                  <ol>
                    <li>Перейдите в список проектов</li>
                    <li>Нажмите кнопку <strong>"Yandex"</strong> 🚀 на проекте</li>
                    <li>Запустите парсинг</li>
                  </ol>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Table
            columns={columns}
            dataSource={tasks}
            loading={loading}
            rowKey="task_id"
            size="small"
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Всего: ${total} задач`,
            }}
          />
        </Card>
      </Space>

      {/* Results Drawer */}
      <Drawer
        title={
          <Space>
            <EyeOutlined />
            <span>Результаты парсинга</span>
          </Space>
        }
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setSelectedTask(null)
          setResult(null)
        }}
        width={900}
        footer={
          result && (
            <Space direction="vertical" style={{ width: '100%' }}>
              {uploading && (
                <Progress 
                  percent={Math.round(uploadProgress)} 
                  status={uploadProgress === 100 ? 'success' : 'active'}
                  format={() => `${Math.round(uploadProgress)}%`}
                />
              )}
              {uploadStatus && (
                <Text type="secondary">{uploadStatus}</Text>
              )}
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleUploadToVDL}
                  loading={uploading}
                  disabled={uploading}
                  size="large"
                >
                  {uploading ? 'Загрузка...' : 'Загрузить в VDL'}
                </Button>
                <Button
                  onClick={() => setDrawerVisible(false)}
                  disabled={uploading}
                >
                  Закрыть
                </Button>
              </Space>
            </Space>
          )
        }
      >
        {loadingResult ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Progress type="circle" percent={50} status="active" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Загрузка результатов...</Text>
            </div>
          </div>
        ) : result ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Summary */}
            <Alert
              message="Парсинг завершен успешно"
              description={
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Запрос">
                    <Text strong>{result.main_query}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Регион">
                    <Text>{result.region_name || getRegionName(result.region_code)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Время">
                    <Text>{result.duration_seconds} секунд</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Завершено">
                    <Text>{dayjs(result.completed_at).format('DD.MM.YYYY HH:mm')}</Text>
                  </Descriptions.Item>
                  {selectedTask?.task_metadata && (
                    <>
                      {selectedTask.task_metadata.min_clicks_threshold !== undefined && (
                        <Descriptions.Item 
                          label={
                            <Space>
                              <span>Минимум кликов</span>
                              <Tag color={selectedTask.task_metadata.min_clicks_threshold === 0 ? 'warning' : 'blue'} style={{ margin: 0 }}>
                                {selectedTask.task_metadata.min_clicks_threshold === 0 ? 'Без фильтра' : `${selectedTask.task_metadata.min_clicks_threshold}+ кликов`}
                              </Tag>
                            </Space>
                          }
                        >
                          <Text strong>
                            {selectedTask.task_metadata.min_clicks_threshold === 0 
                              ? 'Фильтрация отключена (все запросы)' 
                              : `Запросы с ${selectedTask.task_metadata.min_clicks_threshold} и более кликами включены в анализ`}
                          </Text>
                        </Descriptions.Item>
                      )}
                      {selectedTask.task_metadata.download_all_queries !== undefined && (
                        <Descriptions.Item 
                          label={
                            <Space>
                              <span>Режим загрузки</span>
                              <Tag 
                                color={selectedTask.task_metadata.download_all_queries ? 'red' : 'green'} 
                                style={{ margin: 0 }}
                              >
                                {selectedTask.task_metadata.download_all_queries ? 'Экстремальный' : `ТОП-${selectedTask.task_metadata.top_queries_count || 100}`}
                              </Tag>
                            </Space>
                          }
                        >
                          <Text strong>
                            {selectedTask.task_metadata.download_all_queries 
                              ? 'Загружаются все запросы без ограничений (экстремальный режим)' 
                              : `Загружаются только топ-${selectedTask.task_metadata.top_queries_count || 100} популярных запросов (режим ТОП-N)`}
                          </Text>
                        </Descriptions.Item>
                      )}
                      {!selectedTask.task_metadata.download_all_queries && selectedTask.task_metadata.top_queries_count !== undefined && (
                        <Descriptions.Item label="Количество в ТОП-N">
                          <Text strong>{selectedTask.task_metadata.top_queries_count} запросов</Text>
                        </Descriptions.Item>
                      )}
                    </>
                  )}
                </Descriptions>
              }
              type="success"
              showIcon
            />

            {/* Statistics */}
            <Card size="small" title="📊 Статистика">
              <Space size="large">
                <div>
                  <Text type="secondary">Доменов:</Text>
                  <br />
                  <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                    {result.domains_count}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">URL:</Text>
                  <br />
                  <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                    {result.urls_count}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Запросов:</Text>
                  <br />
                  <Text strong style={{ fontSize: 24, color: '#faad14' }}>
                    {result.queries_count}
                  </Text>
                </div>
              </Space>
            </Card>

            {/* Top Domains */}
            <Card size="small" title="🏆 ТОП-10 доменов">
              <Table
                dataSource={result.domains?.slice(0, 10) || []}
                columns={[
                  {
                    title: '#',
                    key: 'index',
                    width: 50,
                    render: (_: any, __: any, index: number) => index + 1,
                  },
                  {
                    title: 'Домен',
                    dataIndex: 'host',
                    key: 'host',
                    ellipsis: true,
                  },
                  {
                    title: 'Клики',
                    dataIndex: 'total_clicks',
                    key: 'total_clicks',
                    width: 100,
                    render: (value: number) => value?.toLocaleString() || 0,
                  },
                  {
                    title: 'Запросов',
                    dataIndex: 'queries_count',
                    key: 'queries_count',
                    width: 100,
                  },
                  {
                    title: 'Позиция',
                    dataIndex: 'average_position',
                    key: 'average_position',
                    width: 100,
                    render: (value: number) => value?.toFixed(1) || '0.0',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="host"
              />
            </Card>

            {/* Top URLs */}
            <Card size="small" title="🔗 ТОП-10 URL">
              <Table
                dataSource={result.urls?.slice(0, 10) || []}
                columns={[
                  {
                    title: '#',
                    key: 'index',
                    width: 50,
                    render: (_: any, __: any, index: number) => index + 1,
                  },
                  {
                    title: 'URL',
                    dataIndex: 'url',
                    key: 'url',
                    ellipsis: true,
                    render: (url: string) => (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                      </a>
                    ),
                  },
                  {
                    title: 'Запросов',
                    dataIndex: 'queries',
                    key: 'queries',
                    width: 100,
                  },
                  {
                    title: 'Позиция',
                    dataIndex: 'average_position',
                    key: 'average_position',
                    width: 100,
                    render: (value: number) => value?.toFixed(1) || '0.0',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="url"
              />
            </Card>
          </Space>
        ) : (
          <Alert
            message="Результаты недоступны"
            description="Не удалось загрузить результаты парсинга"
            type="warning"
            showIcon
          />
        )}
      </Drawer>

      {/* Workflow Modal */}
      <ParsingWorkflowModal
        visible={workflowVisible}
        taskId={selectedTask?.task_id || null}
        projectId={projectId}
        projectCrmId={projectCrmId || null}
        projectName={projectName || selectedTask?.project_name || null}
        initialDomains={result?.domains || []}
        parsingResult={result}
        onClose={() => {
          setWorkflowVisible(false)
          setSelectedTask(null)
          setResult(null)
        }}
        onComplete={() => {
          setWorkflowVisible(false)
          setSelectedTask(null)
          setResult(null)
          loadTasks() // Refresh list
        }}
      />
    </div>
  )
}

export default ParsingHistoryTab

